<script lang="ts" setup>
import {
  ref,
  watch,
  PropType,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";

const menuOverlay = ref();

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  refElem: {
    type: Object as PropType<HTMLElement | null>,
    required: false,
  },
});

watch(props, () => {
  if (!menuOverlay.value || !props.refElem) {
    return;
  }

  if (props.active) {
    menuOverlay.value.classList.add("active");
    positionOverlay();
  } else {
    menuOverlay.value.classList.remove("active");
  }
});

const positionOverlay = () => {
  if (props.refElem) {
    const pos = props.refElem.getBoundingClientRect();
    //menuOverlay.value.style.minWidth = `${pos.width}px`;
    menuOverlay.value.style.top = `${pos.top + 40 + window.scrollY}px`;

    menuOverlay.value.style.left = `${
      pos.x - menuOverlay.value.offsetWidth + pos.width
    }px`;
  }
};

const emit = defineEmits(["close"]);

const close = () => {
  emit("close");
  void nextTick(() => props.refElem?.focus());
};

const handleEscape = (event: KeyboardEvent) => {
  if (
    event.key !== "Escape" ||
    !props.active ||
    event.defaultPrevented ||
    document.querySelector(".modal.active")
  ) {
    return;
  }

  event.preventDefault();
  close();
};

const resizeObeserver = new ResizeObserver((entries) => {
  for (let entry of entries) {
    positionOverlay();
  }
});

onMounted(() => {
  window.addEventListener("keydown", handleEscape);
  if (menuOverlay.value) {
    resizeObeserver.observe(menuOverlay.value);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscape);
  if (menuOverlay.value) {
    resizeObeserver.unobserve(menuOverlay.value);
  }
});
</script>

<template>
  <div class="menu_overlay" ref="menuOverlay">
    <div>
      <div class="header">
        <button
          type="button"
          class="icon"
          :aria-label="$t('cancel')"
          @click="close"
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
        <slot name="header"></slot>
      </div>

      <div class="body">
        <slot name="body"></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.menu_overlay {
  display: none;
  border: 1px solid var(--classic-border-subtle, #ebebeb);
  padding: 0.8em 1.5em 1.2em 1.5em;
  position: absolute;
  background: var(--classic-surface, #fff);
  font-size: 0.9rem;
  box-shadow: 0 4px 12px 0 #0000001a;
  z-index: 99;
}

.menu_overlay.active {
  display: block;
}

.header {
  font-weight: 600;
  line-height: 48px;
}

.header .icon {
  float: right;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.header .icon.left {
  float: left;
  margin-right: 5px;
}

.header .icon .material-symbols-outlined {
  vertical-align: middle;
  font-size: 1.3em;
}

.body {
  display: block;
}
</style>
